import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-55eede36"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "json-preview" }
const _hoisted_2 = {
  key: 0,
  class: "json-tooltip-text"
}

import { ref, computed, onUnmounted } from "vue";

interface Props {
  value: string | object | null | undefined;
  maxLength?: number;
  showTooltip?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'JsonWithTooltip',
  props: {
    value: {},
    maxLength: { default: 50 },
    showTooltip: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props;

const tooltipContainerRef = ref<HTMLElement | null>(null);
let scrollHandler: (() => void) | null = null;
let currentParentNode: HTMLElement | null = null;
let scrollTimeout: number | null = null;
let mutationObserver: MutationObserver | null = null;

const formatJson = (value: string | object | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  try {
    const jsonStr = typeof value === "string" ? value : JSON.stringify(value);
    const parsed = JSON.parse(jsonStr);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return typeof value === "string" ? value : String(value);
  }
};

const fullText = computed(() => {
  const formatted = formatJson(props.value);
  return formatted !== "—" ? formatted : null;
});

const previewText = computed(() => {
  const formatted = formatJson(props.value);
  if (formatted === "—" || formatted.length <= props.maxLength) {
    return formatted;
  }
  return formatted.substring(0, props.maxLength) + "...";
});

const isTooltipVisible = (tooltipElement: HTMLElement): boolean => {
  const computedStyle = window.getComputedStyle(tooltipElement);
  return computedStyle.visibility !== "hidden" && computedStyle.opacity !== "0";
};

const getTooltipDimensions = (tooltipElement: HTMLElement) => {
  let width = tooltipElement.offsetWidth;
  let height = tooltipElement.offsetHeight;

  if (!width || !height || width === 0 || height === 0) {
    const rect = tooltipElement.getBoundingClientRect();
    width = rect.width || 250;
    height = rect.height || 100;
  }

  return {
    width: Math.max(width, 250),
    height: Math.max(height, 50),
  };
};

const calculateTooltipX = (
  parentLeft: number,
  parentWidth: number,
  tooltipWidth: number,
  docWidth: number
): number => {
  const MARGIN = 20;
  let x = parentLeft;

  if (tooltipWidth > docWidth - MARGIN * 2) {
    x = Math.max(
      MARGIN,
      Math.min(
        docWidth - tooltipWidth - MARGIN,
        parentLeft - (tooltipWidth - parentWidth) / 2
      )
    );
  } else {
    if (parentLeft + tooltipWidth > docWidth - MARGIN) {
      x = docWidth - tooltipWidth - MARGIN;
    }
    if (x < MARGIN) {
      x = MARGIN;
    }
  }

  return x;
};

const calculateTooltipY = (
  parentTop: number,
  parentBottom: number,
  tooltipHeight: number,
  docHeight: number
): number => {
  const MARGIN = 20;
  const OFFSET = 10;
  let y = parentBottom + OFFSET;

  if (y + tooltipHeight > docHeight - MARGIN) {
    y = parentTop - tooltipHeight - OFFSET;
  }
  if (y < MARGIN) {
    y = MARGIN;
  }

  return y;
};

const updateTooltipPosition = () => {
  if (!currentParentNode) return;

  const tooltipElement = currentParentNode.querySelector(
    ".json-tooltip-text"
  ) as HTMLElement;
  if (!tooltipElement || !isTooltipVisible(tooltipElement)) return;

  const docWidth = document.documentElement.clientWidth;
  const docHeight = document.documentElement.clientHeight;
  const rectParent = currentParentNode.getBoundingClientRect();
  const { width: tooltipWidth, height: tooltipHeight } =
    getTooltipDimensions(tooltipElement);

  const x = calculateTooltipX(
    rectParent.left,
    rectParent.width,
    tooltipWidth,
    docWidth
  );
  const y = calculateTooltipY(
    rectParent.top,
    rectParent.bottom,
    tooltipHeight,
    docHeight
  );

  tooltipElement.style.top = `${y}px`;
  tooltipElement.style.left = `${x}px`;
};

let rafId: number | null = null;
let tableScrollElement: HTMLElement | null = null;

const clearTooltipPosition = () => {
  currentParentNode = null;
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
    scrollTimeout = null;
  }
  if (scrollHandler) {
    window.removeEventListener("scroll", scrollHandler, true);
    if (tableScrollElement) {
      tableScrollElement.removeEventListener("scroll", scrollHandler, true);
    }
    scrollHandler = null;
    tableScrollElement = null;
  }
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }
};

const createPositionUpdater = () => {
  return () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(() => {
      updateTooltipPosition();
      rafId = null;
    });
  };
};

const createScrollHandler = (updatePosition: () => void): (() => void) => {
  return () => {
    if (scrollTimeout) {
      return;
    }
    scrollTimeout = window.setTimeout(() => {
      updatePosition();
      scrollTimeout = null;
    }, 16);
  };
};

const removeOldScrollHandlers = () => {
  if (scrollHandler) {
    window.removeEventListener("scroll", scrollHandler, true);
    if (tableScrollElement) {
      tableScrollElement.removeEventListener("scroll", scrollHandler, true);
    }
  }
};

const findScrollContainer = (parentNode: HTMLElement): HTMLElement | null => {
  let element: HTMLElement | null = parentNode;

  for (let i = 0; i < 10 && element; i++) {
    const computedStyle = window.getComputedStyle(element);
    const isScrollable =
      computedStyle.overflow === "auto" ||
      computedStyle.overflow === "scroll" ||
      computedStyle.overflowY === "auto" ||
      computedStyle.overflowY === "scroll";

    const hasScrollClass =
      element.classList.contains("p-datatable-scrollable-body") ||
      element.classList.contains("p-datatable-scrollable-wrapper") ||
      element.classList.contains("p-datatable-wrapper") ||
      element.classList.contains("scroll-table");

    if (isScrollable || hasScrollClass) {
      return element;
    }
    element = element.parentElement;
  }

  const tableWrapper = parentNode.closest(
    ".p-datatable-wrapper, .scroll-table"
  );
  const scrollableBody = parentNode.closest(".p-datatable-scrollable-body");
  const scrollableWrapper = parentNode.closest(
    ".p-datatable-scrollable-wrapper"
  );

  return (scrollableBody || scrollableWrapper || tableWrapper) as HTMLElement;
};

const setupScrollListeners = (scrollHandler: () => void) => {
  window.addEventListener("scroll", scrollHandler, true);

  if (tableScrollElement) {
    tableScrollElement.addEventListener("scroll", scrollHandler, true);
  }
};

const setupMutationObserver = (updatePosition: () => void) => {
  if (mutationObserver) {
    mutationObserver.disconnect();
  }

  mutationObserver = new MutationObserver(updatePosition);

  if (currentParentNode) {
    mutationObserver.observe(currentParentNode, {
      attributes: true,
      attributeFilter: ["style", "class"],
      subtree: false,
    });

    if (tableScrollElement) {
      mutationObserver.observe(tableScrollElement, {
        attributes: true,
        attributeFilter: ["style"],
        subtree: false,
      });
    }
  }
};

const schedulePositionUpdates = (updatePosition: () => void) => {
  setTimeout(updatePosition, 10);
  setTimeout(updatePosition, 50);
  setTimeout(updatePosition, 150);
};

const setTooltipPosition = (event: MouseEvent) => {
  const parentNode = event.currentTarget as HTMLElement;
  if (!parentNode || !parentNode.classList.contains("json-tooltip-in-table")) {
    return;
  }

  currentParentNode = parentNode;
  tooltipContainerRef.value = parentNode;

  const updatePosition = createPositionUpdater();
  const scrollHandlerInstance = createScrollHandler(updatePosition);

  removeOldScrollHandlers();
  tableScrollElement = findScrollContainer(parentNode);
  scrollHandler = scrollHandlerInstance;
  setupScrollListeners(scrollHandler);
  setupMutationObserver(updatePosition);
  schedulePositionUpdates(updatePosition);
};

onUnmounted(() => {
  if (rafId) {
    cancelAnimationFrame(rafId);
  }
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }
  if (scrollHandler) {
    window.removeEventListener("scroll", scrollHandler, true);
    if (tableScrollElement) {
      tableScrollElement.removeEventListener("scroll", scrollHandler, true);
    }
    scrollHandler = null;
    tableScrollElement = null;
  }
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }
  currentParentNode = null;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "tooltipContainerRef",
    ref: tooltipContainerRef,
    class: _normalizeClass(["json-tooltip-in-table", { 'no-tooltip': !_ctx.showTooltip }]),
    onMouseenter: setTooltipPosition,
    onMouseleave: clearTooltipPosition
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("pre", null, _toDisplayString(previewText.value), 1)
    ]),
    (_ctx.showTooltip && fullText.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("pre", null, _toDisplayString(fullText.value), 1)
        ]))
      : _createCommentVNode("", true)
  ], 34))
}
}

})